import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * ModernCard — the foundational surface. Clean white card, soft hairline
 * border, subtle signature shadow, consistent rounding. Compose with the
 * Header/Body/Footer slots so every card across the app has the same anatomy.
 */
export function ModernCard({
  className,
  interactive = false,
  ...props
}: HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius)] border border-border bg-card text-card-foreground shadow-[var(--mui-shadow-card)]',
        interactive &&
          'cursor-pointer transition-shadow hover:shadow-[var(--mui-shadow-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className,
      )}
      {...props}
    />
  );
}

export function ModernCardHeader({
  title,
  description,
  icon,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 border-b border-border px-5 py-4',
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        {icon && (
          <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[calc(var(--radius)-6px)] bg-muted text-muted-foreground">
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-foreground">{title}</h3>
          {description && (
            <p className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function ModernCardBody({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 py-4', className)} {...props} />;
}

export function ModernCardFooter({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-t border-border bg-muted/30 px-5 py-3',
        className,
      )}
      {...props}
    />
  );
}
